/** @file Viewport runtime: stores set a dirty flag, frame() draws once per change, pointers route to tools. */
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import { viewCamera } from '@pose2d/editor-geometry/viewCamera';
import type { Id } from '@pose2d/format/types';
import type { Vec2 } from '@pose2d/math/types';
import { mixPreviewStore } from '../../playback/mixPreviewStore';
import { cameraStore } from '../../stores/cameraStore';
import { compiledStore } from '../../stores/compiledStore';
import { docStore } from '../../stores/docStore';
import { ghostStore } from '../../stores/ghostStore';
import { playheadStore } from '../../stores/playheadStore';
import { selectionStore } from '../../stores/selectionStore';
import { toolStore } from '../../stores/toolStore';
import { cameraOf } from './cameraOf';
import { drawFrame } from './drawFrame';
import { pickAt } from './pickAt';
import { routePointer } from './routePointer';
import { createRuntimeState, type RuntimeDeps, type RuntimeState } from './runtimeState';
import { frameBounds } from './scene/frameBounds';
import { stopMixPreview } from './stopMixPreview';
import { syncGhost } from './syncGhost';
import { syncScene } from './syncScene';
import type { PointerKind, ViewportPointer } from './viewportTypes';
import { viewportStore } from './viewportStore';

/** What mountViewport, viewportApi and the bindings call. */
export interface ViewportRuntime {
  resize(width: number, height: number, dpr: number): void;
  frame(): boolean;
  pointer(kind: PointerKind, p: ViewportPointer): void;
  wheel(sx: number, sy: number, deltaY: number): void;
  pick(sx: number, sy: number): { bone: Id | null; slot: Id | null };
  screenToWorld(sx: number, sy: number): Vec2;
  frameSelection(): void;
  focusBone(id: Id): boolean;
  commitUnkeyed(): void;
  cancel(): void;
  dispose(): void;
}

const ZOOM_PER_WHEEL_PX = 1.0015;
const FRAME_PAD_PX = 48;
/** Offsets of tx and ty inside a WORLD_STRIDE block (world: a, b, c, d, tx, ty). */
const WORLD_TX = 4;
const WORLD_TY = 5;
const NO_POINTER: ViewportPointer = { sx: 0, sy: 0, button: 0, ctrl: false, shift: false, alt: false };

/** The unkeyed pose belongs to one frame of one animation in Animate mode (R-E1-07); `base` goes with it, so a
 *  running gesture (or its Escape) never brings it back. */
function dropUnkeyed(s: RuntimeState): void {
  if (viewportStore.getState().unkeyed.length === 0) return;
  s.preview.clear();
  s.base.clear();
  viewportStore.setState({ unkeyed: [], notice: null });
}

/** A Setup/Animate switch or another animation ends a running drag as Escape would: its batch was aimed at the old
 *  target (fixed at press), so it is neither committed nor held. A mode switch also ends a mix preview. */
function retarget(s: RuntimeState, mode: boolean): void {
  if (s.gesture !== null) routePointer(s, 'cancel', NO_POINTER);
  if (mode) stopMixPreview();
  dropUnkeyed(s);
}

/** Every store the frame reads marks it dirty (R-E1-02, W3-30); another ghost is rebuilt (W3-33); unsubscribers. */
function subscribe(s: RuntimeState): (() => void)[] {
  const touch = (): void => { s.dirty = true; };
  return [
    compiledStore.subscribe(() => { syncScene(s); }), cameraStore.subscribe(touch), selectionStore.subscribe(touch), viewportStore.subscribe(touch),
    mixPreviewStore.subscribe(touch), docStore.subscribe(touch),
    toolStore.subscribe((next, prev) => { if (next.mode !== prev.mode) retarget(s, true); touch(); }),
    playheadStore.subscribe((next, prev) => {
      if (next.animation !== prev.animation) retarget(s, false);
      else if (next.frame !== prev.frame) dropUnkeyed(s);
      touch();
    }),
    ghostStore.subscribe((next, prev) => { if (next.ghost !== prev.ghost) syncGhost(s); }),
  ];
}

/** Ids under a canvas pixel in the last posed frame. */
function pickIds(s: RuntimeState, sx: number, sy: number): { bone: Id | null; slot: Id | null } {
  if (s.scene === null || s.maps === null) return { bone: null, slot: null };
  const hit = pickAt({ ...s.scene, maps: s.maps, camera: cameraOf(s) }, sx, sy);
  return { bone: s.maps.boneIds[hit.bone] ?? null, slot: s.maps.slotIds[hit.slot] ?? null };
}

/** Bounds of the selected bones, or of every bone when none is selected. */
function selectionBounds(s: RuntimeState): number[] | null {
  if (s.scene === null || s.maps === null) return null;
  const { maps, scene } = s;
  const chosen = selectionStore.getState().bones.flatMap((id) => { const i = maps.boneIndex.get(id); return i === undefined ? [] : [i]; });
  return frameBounds(scene.pose, scene.data, chosen.length > 0 ? chosen : scene.data.bones.map((_, i) => i));
}

/** Centres the camera on a bone's joint in the last posed frame; false for an unknown bone. */
function focusBone(s: RuntimeState, id: Id): boolean {
  const i = s.maps?.boneIndex.get(id);
  const w = s.scene?.pose.world;
  if (i === undefined || w === undefined) return false;
  const base = i * POSE_LAYOUT.WORLD_STRIDE;
  cameraStore.setState({ x: w[base + WORLD_TX] ?? 0, y: w[base + WORLD_TY] ?? 0 });
  return true;
}

/** Commits the held Animate-mode keys once (Key, `KeyK`) and clears the held pose; a refused batch (the commit
 *  leaves an error notice) keeps both, and the notice, so the user sees why and can retry or undo. */
function commitUnkeyed(s: RuntimeState): void {
  const { unkeyed } = viewportStore.getState();
  if (unkeyed.length === 0) return;
  viewportStore.setState({ notice: null });
  s.deps.commit(unkeyed);
  if (viewportStore.getState().notice?.kind === 'error') return;
  s.preview.clear();
  s.base.clear();
  viewportStore.setState({ unkeyed: [] });
}

/** A held pose lives in one runtime's preview: none survives a remount or another project. */
function forgetUnkeyed(): void {
  if (viewportStore.getState().unkeyed.length > 0) viewportStore.setState({ unkeyed: [], notice: null });
}

/** Creates the runtime and syncs it with the compiled store at once. */
export function createViewportRuntime(deps: RuntimeDeps): ViewportRuntime {
  const s = createRuntimeState(deps);
  forgetUnkeyed();
  const stops = subscribe(s);
  syncScene(s);
  return {
    resize(width, height, dpr) { Object.assign(s, { width, height, dirty: true }); deps.renderer.resize(width, height, dpr); },
    frame: () => drawFrame(s),
    pointer(kind, p) { routePointer(s, kind, p); },
    wheel(sx, sy, deltaY) { if (s.gesture === null) cameraStore.setState(viewCamera.zoomAt(cameraStore.getState(), s.width, s.height, sx, sy, ZOOM_PER_WHEEL_PX ** -deltaY)); },
    pick: (sx, sy) => pickIds(s, sx, sy),
    screenToWorld: (sx, sy) => viewCamera.screenToWorld(cameraOf(s), sx, sy, [0, 0]),
    frameSelection() { const b = selectionBounds(s); if (b !== null) cameraStore.setState(viewCamera.fit(b, s.width, s.height, FRAME_PAD_PX)); },
    focusBone: (id) => focusBone(s, id),
    commitUnkeyed() { commitUnkeyed(s); },
    cancel() { routePointer(s, 'cancel', NO_POINTER); },
    dispose() { for (const stop of stops) stop(); s.gesture?.cancel(); forgetUnkeyed(); deps.renderer.dispose(); },
  };
}
